import { useListState } from "@/hooks/use-list-state";
import { useState, useMemo, useEffect } from "react";
import { useQueryClient, useQuery, useQueries, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { getMonthColumnsForRange, sortPropertiesParentFirst } from "@/lib/pnl-columns";
import { Link, useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Download, FileText, Copy, ChevronDown, ChevronRight, RefreshCw, Building2, Link2, Loader2, Landmark, ExternalLink, AlertTriangle } from "lucide-react";
import type { Property, ExpenseCategorySettings, PropertyGroup } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DateRangeFilter, computeDateRange, type DatePreset , ALL_DATE_PRESETS } from "@/components/DateRangeFilter";
import { parsePadsplitBreakdown, computePnlProfitForMonth } from "@/lib/pnl-profit";
import { resolvePnlPadsplitCategoriesForMonth } from "@shared/pnl-compute";
import { ExpenseWithDoc, isMercurySource, fmtCurrency, MercuryAccountRow, MercuryAccountsResponse, MercuryCreditSummaryResponse, MercuryCreditAccountRow, MercuryCardTxn, formatBalance, isCreditCard, formatCreditCardBalance, formatLastSynced, mostRecentSyncedAt, formatLastSyncedAbsolute, IncomeRow, INCOME_CATEGORIES, buildPnlIncomeRows, isMercuryCardNearLimit, mercuryCardUtilization, MERCURY_CREDIT_NEAR_LIMIT_UTILIZATION } from "./_shared";
import { usePermissions } from "@/hooks/use-permissions";
import "./financials-contrast.css";

// Task #4039 — local YYYY-MM-DD helpers for the inline credit-card transaction
// window. Defaults to month-to-date so the spend figure ties to the current
// statement period (matching the Cards hub default).
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function monthStartStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function todayStr(): string {
  return ymd(new Date());
}

// Task #4062 — compact range presets for the inline credit-card transaction
// window so hosts can investigate prior statement periods or spikes without
// leaving the P&L panel. Mirrors the Cards hub refetch-on-change behaviour.
type CreditTxnRangePreset = "this_month" | "last_month" | "last_90";

const CREDIT_TXN_RANGE_LABELS: Record<CreditTxnRangePreset, string> = {
  this_month: "This month",
  last_month: "Last month",
  last_90: "Last 90 days",
};

function computeCreditTxnRange(preset: CreditTxnRangePreset): { start: string; end: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  if (preset === "last_month") {
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0); // day-0 of current month = last day of prev month
    return { start: ymd(start), end: ymd(end) };
  }
  if (preset === "last_90") {
    const end = new Date(y, m, now.getDate());
    const start = new Date(end);
    start.setDate(start.getDate() - 89); // inclusive 90-day window
    return { start: ymd(start), end: ymd(end) };
  }
  // this_month (month-to-date)
  return { start: monthStartStr(), end: todayStr() };
}

// Task #4039 — inline recent-transactions detail rendered when a per-card
// credit breakdown row is expanded. A Mercury credit *account* can map to one
// or more *cards* (vendor_credit_cards.mercuryCardId), so we fetch each card's
// transactions in parallel via the existing
// GET /api/app/mercury/cards/:cardId/transactions route (month-to-date), then
// merge and sort them most-recent-first and sum the spend across the cards.
function CreditAccountTransactions({
  accountId,
  cardIds,
  currency,
}: {
  accountId: string;
  cardIds: string[];
  currency: string;
}) {
  const [rangePreset, setRangePreset] = useListState<CreditTxnRangePreset>("profit-loss:credit-range", "this_month");
  const { start, end } = computeCreditTxnRange(rangePreset);
  const rangeLabel = CREDIT_TXN_RANGE_LABELS[rangePreset];
  const fmtMoney = (n: number) => {
    try {
      return n.toLocaleString("en-US", { style: "currency", currency });
    } catch {
      return `$${n.toFixed(2)}`;
    }
  };

  const results = useQueries({
    queries: cardIds.map((cardId) => ({
      queryKey: ["/api/app/mercury/cards", cardId, "transactions", start, end],
      queryFn: async () => {
        const params = new URLSearchParams();
        params.set("start", start);
        params.set("end", end);
        const res = await apiRequest(
          "GET",
          `/api/app/mercury/cards/${cardId}/transactions?${params.toString()}`,
        );
        return res.json() as Promise<{ transactions: MercuryCardTxn[]; rangeSpend: number }>;
      },
      retry: false,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);
  const firstError = results.find((r) => r.isError)?.error;
  // Tag each transaction with its source card so the React key / testid stays
  // unique after merging across cards (Mercury transaction IDs are not
  // guaranteed unique across different cards).
  const txns = results
    .flatMap((r, i) => (r.data?.transactions ?? []).map((t) => ({ ...t, cardId: cardIds[i] })))
    .sort((a, b) => b.date.localeCompare(a.date));
  const rangeSpend = results.reduce((sum, r) => sum + (r.data?.rangeSpend ?? 0), 0);

  return (
    <div className="bg-white px-3 py-3" data-testid={`mercury-credit-transactions-${accountId}`}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Recent transactions
          </span>
          <Select value={rangePreset} onValueChange={(v) => setRangePreset(v as CreditTxnRangePreset)}>
            <SelectTrigger className="h-7 w-[140px] text-xs" data-testid={`select-mercury-credit-transactions-range-${accountId}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This month</SelectItem>
              <SelectItem value="last_month">Last month</SelectItem>
              <SelectItem value="last_90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <span className="text-xs text-gray-500">
          <span className="font-semibold text-gray-900 tabular-nums" data-testid={`text-mercury-credit-transactions-spend-${accountId}`}>
            {fmtMoney(rangeSpend)}
          </span>{" "}
          spent
        </span>
      </div>
      {isLoading ? (
        <div className="space-y-1.5" data-testid={`mercury-credit-transactions-loading-${accountId}`}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-8 rounded bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid={`mercury-credit-transactions-error-${accountId}`}>
          {parseErrorMessage(firstError, "Couldn't load transactions. Try again.")}
        </div>
      ) : txns.length === 0 ? (
        <div className="py-4 text-center text-xs text-gray-400" data-testid={`mercury-credit-transactions-empty-${accountId}`}>
          No transactions for {rangeLabel.toLowerCase()}.
        </div>
      ) : (
        <div className="divide-y divide-gray-100 rounded-md border border-gray-200" data-testid={`mercury-credit-transactions-list-${accountId}`}>
          {txns.map((t) => {
            const isCredit = t.amount < 0;
            const rowKey = `${t.cardId ?? "card"}-${t.id}`;
            return (
              <div
                key={rowKey}
                className="flex items-center gap-3 px-3 py-2"
                data-testid={`mercury-credit-transaction-${accountId}-${rowKey}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 truncate">
                    {t.vendor ?? t.description ?? "Transaction"}
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                    <span className="tabular-nums">{t.date.slice(0, 10)}</span>
                    {t.category && <span className="text-gray-400">· {t.category}</span>}
                  </p>
                </div>
                <span
                  className={`text-sm font-semibold shrink-0 tabular-nums ${isCredit ? "text-green-600" : "text-gray-900"}`}
                  data-testid={`mercury-credit-transaction-amount-${accountId}-${t.id}`}
                >
                  {isCredit ? "+" : ""}{fmtMoney(Math.abs(t.amount))}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function MercuryAccountsCard() {
  const { canEdit } = usePermissions();
  const readOnly = !canEdit("financials");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  // Task #2309 — persist the "no credit-card access" warning surfaced by the
  // refresh endpoint so it stays visible inline below the account list (not
  // just as a transient toast).
  const [noCreditCardWarning, setNoCreditCardWarning] = useState<string | null>(null);
  // Task #4003 — expand/collapse the per-card breakdown under the aggregate
  // "Mercury Credit Cards" P&L line so hosts can see which card is near its limit.
  const [creditBreakdownOpen, setCreditBreakdownOpen] = useState(false);
  // Task #4039 — which per-card breakdown row is expanded to reveal that card's
  // recent transactions inline (keyed by credit account id; null = none open).
  const [expandedCreditAccountId, setExpandedCreditAccountId] = useState<string | null>(null);

  const accountsQuery = useQuery<MercuryAccountsResponse>({
    queryKey: ["/api/app/mercury/accounts"],
    retry: false,
  });

  // Task #3938 — live credit limit + outstanding balance across the host's
  // Mercury IO Credit Card accounts. Pulled live from the Mercury API (with a
  // short server-side cache); the line stays hidden when there are no credit
  // accounts / no credit scope (accountCount === 0).
  const creditSummaryQuery = useQuery<MercuryCreditSummaryResponse>({
    queryKey: ["/api/app/mercury/credit-summary"],
    retry: false,
    enabled: accountsQuery.data?.connected === true,
  });

  // Task #2130 — surface Mercury-imported expenses that are still
  // categorized as "other" (Uncategorized). Those rows are excluded from
  // P&L totals, so hosts should be nudged to recategorize them.
  const expensesQuery = useQuery<ExpenseWithDoc[]>({
    queryKey: ["/api/app/expenses"],
    enabled: accountsQuery.data?.connected === true,
  });
  const uncategorizedMercuryCount = useMemo(() => {
    const rows = expensesQuery.data ?? [];
    return rows.filter(
      (e) =>
        isMercurySource(e.externalSource) &&
        e.category === "other" &&
        !e.voidedAt &&
        !e.excludedAt &&
        !e.mergedIntoExpenseId,
    ).length;
  }, [expensesQuery.data]);

  // silent-save-exempt: this PATCH .../sync is a user-initiated settings toggle
  // (enable/disable Mercury account sync), not a background sync trigger — it's a
  // real save and intentionally surfaces the global "Saved" pill (plus its own toast).
  const syncToggleMutation = useMutation({
    mutationFn: ({ mercuryAccountId, enabled }: { mercuryAccountId: string; enabled: boolean }) =>
      apiRequest("PATCH", `/api/app/mercury/accounts/${encodeURIComponent(mercuryAccountId)}/sync`, { enabled }).then((r) => r.json()),
    onMutate: async ({ mercuryAccountId, enabled }) => {
      setTogglingIds((prev) => new Set(prev).add(mercuryAccountId));
      await queryClient.cancelQueries({ queryKey: ["/api/app/mercury/accounts"] });
      const prev = queryClient.getQueryData<MercuryAccountsResponse>(["/api/app/mercury/accounts"]);
      queryClient.setQueryData<MercuryAccountsResponse>(["/api/app/mercury/accounts"], (old) => {
        if (!old) return old;
        return {
          ...old,
          accounts: old.accounts.map((a) =>
            a.mercuryAccountId === mercuryAccountId ? { ...a, syncEnabled: enabled } : a,
          ),
        };
      });
      return { prev };
    },
    onSuccess: (_data, { mercuryAccountId, enabled }) => {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(mercuryAccountId);
        return next;
      });
      toast({
        title: enabled ? "Sync enabled" : "Sync paused",
        description: enabled
          ? "Transactions from this account will be imported."
          : "No new transactions will be imported from this account.",
      });
    },
    onError: (_err, { mercuryAccountId }, ctx) => {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(mercuryAccountId);
        return next;
      });
      if (ctx?.prev) {
        queryClient.setQueryData(["/api/app/mercury/accounts"], ctx.prev);
      }
      toast({
        title: "Toggle failed",
        description: "Could not update sync setting. Please try again.",
        variant: "destructive",
      });
    },
  });

  const refreshMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", "/api/app/mercury/accounts/refresh").then((r) => r.json()),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury/accounts"] });
      // Task #2309 — track the inline warning state from each refresh so the
      // amber notice below the account list reflects the latest scan.
      setNoCreditCardWarning(data?.noCreditCardWarning?.message ?? null);
      if (data?.missingCreditCardNote) {
        // Task #2304 — surface a scope warning when Mercury returned accounts
        // but none of them are the IO Credit Card, so the host knows exactly
        // what to fix rather than wondering why the card is missing.
        toast({
          title: "Balances refreshed — IO Credit Card missing",
          description: data.missingCreditCardNote,
          variant: "default",
          duration: 10000,
        });
      } else {
        toast({ title: "Refreshed", description: "Mercury balances updated." });
      }
    },
    onError: (e: unknown) => {
      toast({
        title: "Refresh failed",
        description: parseErrorMessage(e, "Could not refresh Mercury balances."),
        variant: "destructive",
      });
    },
  });

  if (accountsQuery.isLoading) {
    return (
      <div className="border border-gray-200 rounded-lg p-6 mb-6" data-testid="mercury-accounts-loading">
        <Skeleton className="h-6 w-48 mb-3" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  const data = accountsQuery.data;
  if (accountsQuery.isError && !data) {
    return (
      <div className="rounded-lg border bg-card p-6" role="alert" data-testid="mercury-accounts-error">
        <h2 className="font-semibold">Mercury balances unavailable</h2>
        <p className="mt-2 text-sm text-muted-foreground">{parseErrorMessage(accountsQuery.error, "Could not load Mercury balances.")}</p>
        <Button variant="outline" className="mt-3" onClick={() => accountsQuery.refetch()}>Retry</Button>
        <Link to="/app/financials/settings" className="ml-3 text-brand underline">Mercury settings</Link>
      </div>
    );
  }
  if (!data?.connected) {
    return (
      <div className="rounded-lg border bg-card p-6" data-testid="mercury-accounts-disconnected">
        <h2 className="font-semibold">Connect Mercury to view balances</h2>
        <p className="mt-2 text-sm text-muted-foreground">No active Mercury connection. Your existing financial records are unchanged.</p>
        <Link to="/app/financials/settings" className="mt-3 inline-block text-brand underline">Mercury settings</Link>
      </div>
    );
  }

  return (
    <div className="financials-balances border border-gray-200 rounded-lg p-4 sm:p-6 mb-6" data-testid="mercury-accounts-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-950/40 rounded-lg flex items-center justify-center flex-shrink-0">
          <Landmark className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-gray-900">Mercury accounts</h3>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => refreshMutation.mutate()}
              disabled={readOnly || refreshMutation.isPending}
              data-testid="button-mercury-accounts-refresh"
            >
              {refreshMutation.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Refreshing</>
              ) : (
                <><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh</>
              )}
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Live balances pushed from Mercury's webhook stream. Click an account to open it in Mercury.
          </p>
          {accountsQuery.isError && (
            <div className="mt-3 text-sm" role="alert">
              Showing previously loaded balances. Could not load the latest account data.
              <Button variant="ghost" size="sm" onClick={() => accountsQuery.refetch()}>Retry</Button>
            </div>
          )}

          {(() => {
            const latest = mostRecentSyncedAt(data.accounts);
            const label = formatLastSyncedAbsolute(latest);
            return (
              <p
                className="text-xs text-gray-400 mt-1"
                title={latest ? latest.toLocaleString() : undefined}
                data-testid="text-mercury-section-last-synced"
              >
                {label ? `Last synced ${label}` : "Not synced yet"}
              </p>
            );
          })()}

          {uncategorizedMercuryCount > 0 && (
            <Link
              to={`/app/financials/expenses?source=mercury&category=other`}
              className="mt-3 flex items-center gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
              data-testid="link-mercury-uncategorized-warning"
            >
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              <span data-testid="text-mercury-uncategorized-count">
                {uncategorizedMercuryCount} uncategorized {uncategorizedMercuryCount === 1 ? "expense" : "expenses"} — excluded from P&amp;L totals
              </span>
              <ExternalLink className="h-3.5 w-3.5 text-amber-600 ml-auto shrink-0" />
            </Link>
          )}

          {(() => {
            const cardAccounts = data.accounts.filter((a) => isCreditCard(a.kind));
            const cashAccounts = data.accounts.filter((a) => !isCreditCard(a.kind));
            if (cardAccounts.length === 0) return null;
            const sumBalance = (rows: MercuryAccountRow[]) =>
              rows.reduce((sum, a) => {
                const n = parseFloat(a.currentBalance ?? "");
                return sum + (isNaN(n) ? 0 : n);
              }, 0);
            const currency = data.accounts[0]?.currency || "USD";
            const fmt = (n: number) => {
              try {
                return n.toLocaleString("en-US", { style: "currency", currency });
              } catch {
                return `$${n.toFixed(2)}`;
              }
            };
            const cashTotal = sumBalance(cashAccounts);
            const cardsTotal = Math.abs(sumBalance(cardAccounts));
            const netPosition = cashTotal - cardsTotal;
            return (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="mercury-summary-tiles">
                <div className="rounded-md border border-gray-100 bg-gray-50 p-3" data-testid="tile-mercury-cash-on-hand">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cash on hand</div>
                  <div className="mt-1 text-lg font-semibold text-gray-900 tabular-nums" data-testid="text-mercury-cash-on-hand">
                    {fmt(cashTotal)}
                  </div>
                </div>
                <div className="rounded-md border border-red-100 bg-red-50 p-3" data-testid="tile-mercury-credit-cards-owed">
                  <div className="text-xs font-medium text-red-600 uppercase tracking-wide">Credit cards owed</div>
                  <div className="mt-1 text-lg font-semibold text-red-600 tabular-nums" data-testid="text-mercury-credit-cards-owed">
                    −{fmt(cardsTotal)}
                  </div>
                </div>
                <div className="rounded-md border border-gray-100 bg-gray-50 p-3" data-testid="tile-mercury-net-position">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Net Mercury position</div>
                  <div
                    className={`mt-1 text-lg font-semibold tabular-nums ${netPosition < 0 ? "text-red-600" : "text-gray-900"}`}
                    data-testid="text-mercury-net-position"
                  >
                    {netPosition < 0 ? `−${fmt(Math.abs(netPosition))}` : fmt(netPosition)}
                  </div>
                </div>
              </div>
            );
          })()}

          {(() => {
            const cs = creditSummaryQuery.data;
            if (!cs || !cs.connected || cs.accountCount === 0) return null;
            const currency = cs.currency || "USD";
            const fmt = (n: number) => {
              try {
                return n.toLocaleString("en-US", { style: "currency", currency });
              } catch {
                return `$${n.toFixed(2)}`;
              }
            };
            // Task #4003 — per-card breakdown rows. Only show the expand control
            // when there is at least one card row to reveal.
            const perCard = cs.accounts ?? [];
            const canExpand = perCard.length > 0;
            const isOpen = canExpand && creditBreakdownOpen;
            // Task #4004 — flag a card whose utilization has crossed the default
            // >80% threshold so hosts can spot which card is near its limit at a
            // glance and avoid declined vendor payments.
            const isNearLimit = (row: MercuryCreditAccountRow) => isMercuryCardNearLimit(row);
            // Cards over the threshold, surfaced as an always-visible warning
            // above the (collapsible) per-card breakdown — sorted most-utilized
            // first so the worst offender leads.
            const nearLimitCards = perCard
              .filter(isNearLimit)
              .sort((a, b) => mercuryCardUtilization(b) - mercuryCardUtilization(a));
            const hasNearLimitWarning = nearLimitCards.length > 0;
            const cardLabel = (row: MercuryCreditAccountRow) =>
              `${row.nickname || "Mercury card"}${row.last4 ? ` ••••${row.last4}` : ""}`;
            return (
              <div className="mt-4 rounded-md border border-red-100 bg-red-50/60" data-testid="mercury-credit-cards-line">
                <button
                  type="button"
                  className={`flex w-full items-center justify-between gap-3 p-3 text-left ${canExpand ? "hover:bg-red-50 transition-colors" : ""} ${isOpen || hasNearLimitWarning ? "rounded-t-md" : "rounded-md"}`}
                  onClick={canExpand ? () => setCreditBreakdownOpen((v) => !v) : undefined}
                  disabled={!canExpand}
                  aria-expanded={isOpen}
                  data-testid="button-mercury-credit-breakdown-toggle"
                >
                  <div className="min-w-0 flex items-center gap-2">
                    {canExpand && (
                      isOpen
                        ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                        : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900">Mercury Credit Cards</div>
                      <div className="text-xs text-gray-500">
                        Live across {cs.accountCount} {cs.accountCount === 1 ? "card account" : "card accounts"} ·{" "}
                        <span data-testid="text-mercury-credit-available" className="tabular-nums">{fmt(cs.totalAvailableCredit)}</span> available
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-red-600 tabular-nums" data-testid="text-mercury-credit-outstanding">
                      −{fmt(cs.totalOutstandingBalance)} owed
                    </div>
                    <div className="text-xs text-gray-500 tabular-nums" data-testid="text-mercury-credit-limit">
                      of {fmt(cs.totalCreditLimit)} limit
                    </div>
                  </div>
                </button>
                {hasNearLimitWarning && (
                  <div
                    className={`flex items-start gap-2 border-t border-amber-200 bg-amber-50 px-3 py-2.5 ${isOpen ? "" : "rounded-b-md"}`}
                    data-testid="mercury-credit-near-limit-warning"
                  >
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 text-xs text-amber-800">
                      <span className="font-semibold" data-testid="text-mercury-credit-near-limit-headline">
                        {nearLimitCards.length === 1
                          ? "1 card is near its limit"
                          : `${nearLimitCards.length} cards are near their limit`}
                      </span>
                      <span className="text-amber-700">
                        {" "}— over {Math.round(MERCURY_CREDIT_NEAR_LIMIT_UTILIZATION * 100)}% used. Pay it down to avoid declined payments.
                      </span>
                      <div className="mt-1 space-y-0.5">
                        {nearLimitCards.map((row) => (
                          <div key={row.id} className="tabular-nums" data-testid={`text-mercury-credit-near-limit-${row.id}`}>
                            {cardLabel(row)} · {Math.round(mercuryCardUtilization(row) * 100)}% used ({fmt(row.availableCredit)} left)
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {isOpen && (
                  <div className="divide-y divide-red-100 border-t border-red-100" data-testid="mercury-credit-breakdown">
                    {perCard.map((row) => {
                      const cardCurrency = row.currency || currency;
                      const fmtCard = (n: number) => {
                        try {
                          return n.toLocaleString("en-US", { style: "currency", currency: cardCurrency });
                        } catch {
                          return `$${n.toFixed(2)}`;
                        }
                      };
                      const nearLimit = isNearLimit(row);
                      // Task #4039 — a credit account row is expandable into its
                      // recent transactions only when it maps to at least one
                      // synced Mercury card (cardIds populated for beta hosts).
                      const cardIds = row.cardIds ?? [];
                      const canExpandTxns = cardIds.length > 0;
                      const txnsOpen = canExpandTxns && expandedCreditAccountId === row.id;
                      const RowTag = canExpandTxns ? "button" : "div";
                      return (
                        <div key={row.id} data-testid={`mercury-credit-breakdown-row-${row.id}`}>
                          <RowTag
                            type={canExpandTxns ? "button" : undefined}
                            onClick={
                              canExpandTxns
                                ? () => setExpandedCreditAccountId((cur) => (cur === row.id ? null : row.id))
                                : undefined
                            }
                            aria-expanded={canExpandTxns ? txnsOpen : undefined}
                            className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left ${canExpandTxns ? "hover:bg-red-50/60 transition-colors cursor-pointer" : ""}`}
                            data-testid={`button-mercury-credit-breakdown-row-${row.id}`}
                          >
                            <div className="min-w-0 flex items-center gap-2">
                              {canExpandTxns && (
                                txnsOpen
                                  ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                                  : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                              )}
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900 truncate" data-testid={`text-mercury-credit-card-name-${row.id}`}>
                                    {row.nickname || "Mercury card"}
                                  </span>
                                  {row.last4 && (
                                    <span className="text-xs text-gray-400 tabular-nums">•••• {row.last4}</span>
                                  )}
                                  {nearLimit && (
                                    <span
                                      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 uppercase tracking-wide"
                                      data-testid={`badge-mercury-credit-near-limit-${row.id}`}
                                    >
                                      <AlertTriangle className="h-3 w-3" /> Near limit
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">
                                  <span className="tabular-nums" data-testid={`text-mercury-credit-card-available-${row.id}`}>{fmtCard(row.availableCredit)}</span> available
                                </div>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-sm font-semibold text-red-600 tabular-nums" data-testid={`text-mercury-credit-card-outstanding-${row.id}`}>
                                −{fmtCard(row.outstandingBalance)} owed
                              </div>
                              <div className="text-xs text-gray-500 tabular-nums" data-testid={`text-mercury-credit-card-limit-${row.id}`}>
                                of {fmtCard(row.creditLimit)} limit
                              </div>
                            </div>
                          </RowTag>
                          {txnsOpen && (
                            <CreditAccountTransactions
                              accountId={row.id}
                              cardIds={cardIds}
                              currency={cardCurrency}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
          {creditSummaryQuery.isLoading && <p className="mt-3 text-sm text-muted-foreground">Loading credit balances…</p>}
          {creditSummaryQuery.isError && (
            <div className="mt-3 text-sm" role="alert" data-testid="mercury-credit-summary-error">
              Credit balances could not be loaded.
              <Button variant="ghost" size="sm" onClick={() => creditSummaryQuery.refetch()}>Retry</Button>
            </div>
          )}

          {data.accounts.length === 0 ? (
            <div
              className="mt-4 rounded-md border border-dashed border-gray-200 bg-gray-50 p-4"
              data-testid="mercury-accounts-empty"
            >
              <p className="text-sm font-medium text-gray-900">Waiting for Mercury's first balance event</p>
              <p className="text-sm text-gray-500 mt-1">
                Mercury pushes balances whenever an account's balance changes — this usually arrives within a few
                minutes of your first transaction. You can also pull them in now by tapping Refresh above, or revisit{" "}
                <Link
                  to="/app/financials/settings"
                  className="text-indigo-600 hover:text-indigo-700 underline"
                  data-testid="link-mercury-empty-settings"
                >
                  your Mercury connection
                </Link>{" "}
                if something looks off.
              </p>
            </div>
          ) : (
            <div className="mt-4 divide-y divide-gray-100 border border-gray-100 rounded-md">
              {data.accounts.map((acct) => {
                const isCard = isCreditCard(acct.kind);
                const isToggling = togglingIds.has(acct.mercuryAccountId);
                return (
                  <div
                    key={acct.id}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                    data-testid={isCard ? `mercury-account-row-card-${acct.mercuryAccountId}` : `mercury-account-row-${acct.mercuryAccountId}`}
                  >
                    <a
                      href={`https://app.mercury.com/accounts/${encodeURIComponent(acct.mercuryAccountId)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-w-0 flex-1 group"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate group-hover:text-indigo-600 transition-colors" data-testid={`text-mercury-nickname-${acct.mercuryAccountId}`}>
                          {acct.nickname || "Mercury account"}
                        </span>
                        {isCard ? (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 uppercase tracking-wide">
                            Credit card
                          </span>
                        ) : acct.kind ? (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 uppercase tracking-wide">
                            {acct.kind}
                          </span>
                        ) : null}
                        {acct.accountNumberLast4 && (
                          <span className="text-xs text-gray-400">••••{acct.accountNumberLast4}</span>
                        )}
                        <ExternalLink className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div
                        className="text-xs text-gray-500 mt-0.5"
                        title={acct.lastSyncedAt ?? undefined}
                        data-testid={`text-mercury-last-synced-${acct.mercuryAccountId}`}
                      >
                        {formatLastSynced(acct.lastSyncedAt)}
                      </div>
                    </a>
                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      {isCard ? (
                        <span
                          className="text-sm font-semibold text-red-600 tabular-nums"
                          data-testid={`text-mercury-balance-card-${acct.mercuryAccountId}`}
                        >
                          {formatCreditCardBalance(acct.currentBalance, acct.currency)}
                        </span>
                      ) : (
                        <span
                          className="text-sm font-semibold text-gray-900 tabular-nums"
                          data-testid={`text-mercury-balance-${acct.mercuryAccountId}`}
                        >
                          {formatBalance(acct.currentBalance, acct.currency)}
                        </span>
                      )}
                      <Switch
                        checked={acct.syncEnabled}
                        disabled={readOnly || isToggling}
                        onCheckedChange={(checked) =>
                          syncToggleMutation.mutate({ mercuryAccountId: acct.mercuryAccountId, enabled: checked })
                        }
                        data-testid={`switch-mercury-sync-${acct.mercuryAccountId}`}
                        aria-label={acct.syncEnabled ? "Disable sync for this account" : "Enable sync for this account"}
                        title={acct.syncEnabled ? "Syncing — click to pause" : "Paused — click to resume"}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {noCreditCardWarning && (
            <div
              className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
              data-testid="notice-mercury-no-credit-card-warning"
            >
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <span data-testid="text-mercury-no-credit-card-warning">{noCreditCardWarning}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Task #2301 — Small inline badge that marks a row as PadSplit-sourced. */
function PadSplitBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 ml-1.5 align-middle leading-none">
      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      PadSplit
    </span>
  );
}

export function ProfitAndLoss({ properties, initialPropertyId }: { properties: Property[]; initialPropertyId?: string }) {
  const [pathname, navigate] = useLocation();
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  const { toast } = useToast();

  const propertyFilter = searchParams.get("propertyId") || initialPropertyId || "all";
  // Task #3740 — scope the P&L to a single CRM owner (all properties in the
  // owner's property groups). Mutually exclusive with the property filter.
  const ownerFilter = searchParams.get("ownerId") || "all";
  // Task #2208 — replace the legacy 3/6/12-month Select with the shared
  // DateRangeFilter component, persisting the same datePreset/dateFrom/dateTo
  // URL params used by Expenses (#2205) and Income.
  const VALID_PNL_DATE_PRESETS: readonly DatePreset[] = ALL_DATE_PRESETS;
  const rawPnlDatePreset = searchParams.get("datePreset") || "";
  const datePreset = (VALID_PNL_DATE_PRESETS.includes(rawPnlDatePreset as DatePreset) ? rawPnlDatePreset : "") as DatePreset;
  const dateFrom = searchParams.get("dateFrom") || "";
  const dateTo = searchParams.get("dateTo") || "";

  useEffect(() => {
    const initialParams = new URLSearchParams(window.location.search);
    const hasShareableFilters =
      initialParams.has("propertyId") ||
      initialParams.has("ownerId") ||
      initialParams.has("datePreset") ||
      initialParams.has("dateFrom") ||
      initialParams.has("dateTo");
    if (hasShareableFilters) {
      toast({
        title: "Shared view loaded",
        description: "Filters from the link have been applied.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePnlQuery = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
  };

  const setPropertyFilter = (v: string) => {
    updatePnlQuery({ propertyId: v === "all" ? null : v, ownerId: null });
  };
  const setOwnerFilter = (v: string) => {
    updatePnlQuery({ ownerId: v === "all" ? null : v, propertyId: null });
  };
  const setDatePreset = (preset: DatePreset) => {
    updatePnlQuery({ datePreset: preset || null, dateFrom: null, dateTo: null });
  };
  const setDateFrom = (v: string) => updatePnlQuery({ dateFrom: v || null });
  const setDateTo = (v: string) => updatePnlQuery({ dateTo: v || null });

  const handleCopyLink = async () => {
    const next = new URLSearchParams();
    if (propertyFilter !== "all") next.set("propertyId", propertyFilter);
    if (ownerFilter !== "all") next.set("ownerId", ownerFilter);
    if (datePreset) next.set("datePreset", datePreset);
    if (datePreset === "custom" && dateFrom) next.set("dateFrom", dateFrom);
    if (datePreset === "custom" && dateTo) next.set("dateTo", dateTo);
    const qs = next.toString();
    const url = `${window.location.origin}${pathname}${qs ? `?${qs}` : ""}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied", description: "Share this URL to open the same view." });
    } catch {
      toast({
        title: "Couldn't copy link",
        description: "Copy this URL manually: " + url,
        variant: "destructive",
      });
    }
  };

  // Task #3740 — owners (gated PM Pro + owner_portal beta) for the owner filter.
  // The custom queryFn swallows non-OK responses so the filter simply stays
  // hidden for hosts without owner-CRM access instead of throwing.
  const pnlOwnersQuery = useQuery<{ owners: { id: number; name: string }[] }>({
    queryKey: ["/api/app/operations/owners"],
    queryFn: async () => {
      const res = await fetch("/api/app/operations/owners");
      if (!res.ok) return { owners: [] };
      return res.json();
    },
    retry: false,
  });
  const pnlOwners = pnlOwnersQuery.data?.owners ?? [];

  const pnlGroupsQuery = useQuery<PropertyGroup[]>({ queryKey: ["/api/app/property-groups"] });
  const pnlGroups = pnlGroupsQuery.data ?? [];

  // Set of property IDs belonging to the selected owner, via the group-name text
  // match (properties.propertyGroup === propertyGroups.name) on owner-linked
  // groups. null = no owner filter active.
  const ownerPropIdSet = useMemo(() => {
    if (ownerFilter === "all") return null;
    const ownerId = parseInt(ownerFilter, 10);
    if (Number.isNaN(ownerId)) return null;
    const groupNames = new Set(
      pnlGroups.filter((g) => g.ownerId === ownerId).map((g) => (g.name ?? "").toLowerCase()),
    );
    return new Set(
      properties
        .filter((p) => p.propertyGroup && groupNames.has(p.propertyGroup.toLowerCase()))
        .map((p) => p.id),
    );
  }, [ownerFilter, pnlGroups, properties]);

  const pnlDateRange = useMemo(
    () => computeDateRange(datePreset, dateFrom, dateTo),
    [datePreset, dateFrom, dateTo],
  );

  const expensesQuery = useQuery<ExpenseWithDoc[]>({
    queryKey: ["/api/app/expenses"],
    // The PadSplit browser harvest writes this ledger outside the app, so no
    // client mutation exists to invalidate an Infinity-stale cache. Refresh
    // this report's observer whenever it is mounted without changing the
    // application-wide cache policy.
    refetchOnMount: "always",
  });
  const allExpenses = expensesQuery.data ?? [];

  // Task #2311 — category settings used to identify balance sheet custom categories.
  const pnlCatSettingsQuery = useQuery<ExpenseCategorySettings>({ queryKey: ["/api/app/expense-category-settings"] });
  const pnlCatSettings = pnlCatSettingsQuery.data;

  // Task #2172 — Income ledger for the P&L Income section. Fetched unscoped
  // and filtered client-side to mirror how expenses are loaded above; the
  // /api/app/income endpoint returns mercury_credit, padsplit_earnings and
  // manual income rows.
  const incomeQuery = useQuery<IncomeRow[]>({
    queryKey: ["/api/app/income"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/app/income");
      return res.json();
    },
    // See the expense ledger above: daily harvest updates have no in-app
    // invalidation event, and global staleTime is intentionally Infinity.
    refetchOnMount: "always",
  });
  const allIncome = incomeQuery.data ?? [];

  // Task #2136 — count of excluded Mercury transactions for the current
  // property + time-range filter, surfaced under the P&L total so hosts can
  // reconcile against Mercury's own statements.
  type ExcludedMercurySummary = { id: number; date: string; propertyId: number | null; amount: string };
  const excludedMercuryQuery = useQuery<ExcludedMercurySummary[]>({ queryKey: ["/api/app/expenses/excluded-mercury"] });
  const allExcludedMercury = excludedMercuryQuery.data ?? [];

  const filteredExpenses = useMemo(() => {
    // Task #2305 — exclude split parents from P&L totals; only their
    // children count so there's no double-counting across properties.
    const splitParentIdSet = new Set(
      allExpenses.filter((e) => (e as any).splitParentId != null).map((e) => (e as any).splitParentId as number),
    );
    let base = allExpenses.filter((e) => {
      if (splitParentIdSet.has(e.id)) return false;
      const rowDate = (e.date || "").slice(0, 10);
      if (pnlDateRange.from && rowDate < pnlDateRange.from) return false;
      if (pnlDateRange.to && rowDate > pnlDateRange.to) return false;
      return true;
    });
    if (ownerPropIdSet) base = base.filter((e) => e.propertyId !== null && ownerPropIdSet.has(e.propertyId));
    if (propertyFilter === "all") return base;
    const selectedId = parseInt(propertyFilter);
    const selectedProp = properties.find((p) => p.id === selectedId);
    if (!selectedProp) return base.filter((e) => String(e.propertyId) === propertyFilter);
    const childIds = properties.filter((p) => p.parentPropertyId === selectedId).map((p) => p.id);
    const allIds = [selectedId, ...childIds];
    return base.filter((e) => e.propertyId !== null && allIds.includes(e.propertyId));
  }, [allExpenses, propertyFilter, properties, ownerPropIdSet, pnlDateRange]);

  const filteredIncome = useMemo(() => {
    // Task #2305 — same logic as filteredExpenses: exclude split parents.
    const splitParentIdSet = new Set(
      allIncome.filter((r) => (r as any).splitParentId != null).map((r) => (r as any).splitParentId as number),
    );
    let base = allIncome.filter((r) => {
      if (splitParentIdSet.has(r.id)) return false;
      const rowDate = (r.date || "").slice(0, 10);
      if (pnlDateRange.from && rowDate < pnlDateRange.from) return false;
      if (pnlDateRange.to && rowDate > pnlDateRange.to) return false;
      return true;
    });
    if (ownerPropIdSet) base = base.filter((r) => r.propertyId !== null && ownerPropIdSet.has(r.propertyId));
    if (propertyFilter === "all") return base;
    const selectedId = parseInt(propertyFilter);
    const selectedProp = properties.find((p) => p.id === selectedId);
    if (!selectedProp) return base.filter((r) => String(r.propertyId) === propertyFilter);
    const childIds = properties.filter((p) => p.parentPropertyId === selectedId).map((p) => p.id);
    const allIds = [selectedId, ...childIds];
    return base.filter((r) => r.propertyId !== null && allIds.includes(r.propertyId));
  }, [allIncome, propertyFilter, properties, ownerPropIdSet, pnlDateRange]);
  const months = useMemo(
    () => getMonthColumnsForRange(pnlDateRange.from, pnlDateRange.to),
    [pnlDateRange.from, pnlDateRange.to],
  );

  // Task #2136 — Limit the excluded-Mercury count to the same property +
  // time-range window the P&L totals already reflect.
  const excludedMercuryInView = useMemo(() => {
    const monthKeys = new Set(months.map((m) => m.key));
    // Owner and property filters are mutually exclusive; when an owner is
    // selected scope to that owner's properties so the excluded-Mercury count
    // honors the same scope as the income/expense totals on this screen.
    let allowedPropertyIds: Set<number> | null = ownerPropIdSet;
    if (!allowedPropertyIds && propertyFilter !== "all") {
      const selectedId = parseInt(propertyFilter);
      const selectedProp = properties.find((p) => p.id === selectedId);
      if (selectedProp) {
        const childIds = properties.filter((p) => p.parentPropertyId === selectedId).map((p) => p.id);
        allowedPropertyIds = new Set<number>([selectedId, ...childIds]);
      } else if (!Number.isNaN(selectedId)) {
        allowedPropertyIds = new Set<number>([selectedId]);
      }
    }
    return allExcludedMercury.filter((e) => {
      if (allowedPropertyIds) {
        if (e.propertyId === null || !allowedPropertyIds.has(e.propertyId)) return false;
      }
      const monthKey = (e.date || "").slice(0, 7);
      return monthKeys.has(monthKey);
    });
  }, [allExcludedMercury, months, propertyFilter, properties, ownerPropIdSet]);

  const handleViewExcludedMercury = () => {
    const params = new URLSearchParams();
    if (propertyFilter !== "all") params.set("propertyId", propertyFilter);
    params.set("source", "mercury");
    params.set("excluded", "1");
    const qs = params.toString();
    navigate(`/app/financials/expenses${qs ? `?${qs}` : ""}`);
  };

  // Excluded-income count within the current property + date-range window.
  // Uses a dedicated endpoint (includeExcluded=true) so excluded rows are not
  // filtered by storage before reaching this computation — mirroring the
  // excluded-Mercury expense count pattern above.
  type ExcludedIncomeSummary = { id: number; date: string; propertyId: number | null; isCompanyLevel: boolean; amount: string };
  const excludedIncomeSummaryQuery = useQuery<ExcludedIncomeSummary[]>({ queryKey: ["/api/app/income/excluded-summary"] });
  const allExcludedIncomeSummary = excludedIncomeSummaryQuery.data ?? [];

  const excludedIncomeInView = useMemo(() => {
    const monthKeys = new Set(months.map((m) => m.key));
    let allowedPropertyIds: Set<number> | null = ownerPropIdSet;
    if (!allowedPropertyIds && propertyFilter !== "all") {
      const selectedId = parseInt(propertyFilter);
      const selectedProp = properties.find((p) => p.id === selectedId);
      if (selectedProp) {
        const childIds = properties.filter((p) => p.parentPropertyId === selectedId).map((p) => p.id);
        allowedPropertyIds = new Set<number>([selectedId, ...childIds]);
      } else if (!Number.isNaN(selectedId)) {
        allowedPropertyIds = new Set<number>([selectedId]);
      }
    }
    return allExcludedIncomeSummary.filter((r) => {
      if (allowedPropertyIds) {
        // Company-level rows (propertyId=null, isCompanyLevel=true) are excluded from
        // property-scoped P&L totals just like they are excluded from filteredIncome.
        if (r.isCompanyLevel) return false;
        if (r.propertyId === null) return false;
        if (!allowedPropertyIds.has(r.propertyId)) return false;
      }
      const monthKey = (r.date || "").slice(0, 7);
      return monthKeys.has(monthKey);
    });
  }, [allExcludedIncomeSummary, months, propertyFilter, properties, ownerPropIdSet]);

  const handleViewExcludedIncome = () => {
    const params = new URLSearchParams();
    if (propertyFilter !== "all") params.set("inc_propertyIds", propertyFilter);
    params.set("inc_excluded", "1");
    const qs = params.toString();
    navigate(`/app/financials/income${qs ? `?${qs}` : ""}`);
  };

  const pnlData = useMemo(() => {
    const collectionCategories = [
      { key: "membership_dues", label: "Membership dues" },
      { key: "move_in_fees", label: "Move-in fees" },
      { key: "commitment_fees", label: "Commitment fees" },
      { key: "late_fees", label: "Late fees" },
      { key: "other_bills", label: "Other bills" },
    ];

    const expenseCategoryGroups: Record<string, string[]> = {
      "Mortgage": ["mortgage"],
      "Property Tax": ["property_tax"],
      "Insurance": ["insurance"],
      "HOA": ["hoa"],
      "Utilities": ["utilities_electric", "utilities_gas", "utilities_water", "utilities_sewer", "utilities_trash", "utilities_internet"],
      "Repairs & Maintenance": ["repairs_maintenance"],
      "Cleaning": ["cleaning"],
      "Lawn Care / Landscaping": ["lawn_care"],
      "Pest Control": ["pest_control"],
      "Property Management": ["property_management", "padsplit_mgmt_fee"],
      "Furnishings": ["furnishings"],
      "Supplies": ["supplies"],
      "Legal & Professional": ["legal_professional"],
      "Marketing": ["marketing"],
      "Travel": ["travel"],
    };

    // Task #2301 — "charges" moved from Income (adjustments) to Expenses.
    const adjustmentCategories = [
      { key: "credits", label: "Credits" },
      { key: "referral_rewards", label: "Referral rewards" },
    ];
    const padsplitChargeCategories = [
      { key: "charges", label: "Charges" },
    ];

    // Task #2311 — custom categories split into P&L and balance sheet.
    const allCustomCats = ((pnlCatSettings?.customCategories || []) as {label: string; value: string; isBalanceSheet?: boolean}[]);
    const balanceSheetCatValues = new Set(allCustomCats.filter((c) => c.isBalanceSheet).map((c) => c.value));
    const nonBsCustomCats = allCustomCats.filter((c) => !c.isBalanceSheet);

    // Task #3788 — partition company-level (non-property) expenses out of the
    // per-property category rows so they can be surfaced as their own labeled
    // P&L group without double-counting. The helpers below accept an explicit
    // source list (defaulting to all filtered expenses) so the same category
    // math drives both the property-level rows and the Company Operations group.
    const companyExpenses = filteredExpenses.filter((e) => (e as any).isCompanyLevel);
    const propertyExpenses = filteredExpenses.filter((e) => !(e as any).isCompanyLevel);

    const getMonthTotal = (category: string, monthKey: string, list: typeof filteredExpenses = filteredExpenses) => {
      return list
        .filter((e) => e.category === category && e.date.startsWith(monthKey))
        .reduce((sum, e) => sum + parseFloat(e.amount || "0"), 0);
    };

    const getGroupMonthTotal = (cats: string[], monthKey: string, list: typeof filteredExpenses = filteredExpenses) => {
      return list
        .filter((e) => cats.includes(e.category) && e.date.startsWith(monthKey))
        .reduce((sum, e) => sum + parseFloat(e.amount || "0"), 0);
    };

    const getYTD = (category: string, list: typeof filteredExpenses = filteredExpenses) => {
      const year = new Date().getFullYear();
      return list
        .filter((e) => e.category === category && e.date.startsWith(String(year)))
        .reduce((sum, e) => sum + parseFloat(e.amount || "0"), 0);
    };

    const getGroupYTD = (cats: string[], list: typeof filteredExpenses = filteredExpenses) => {
      const year = new Date().getFullYear();
      return list
        .filter((e) => cats.includes(e.category) && e.date.startsWith(String(year)))
        .reduce((sum, e) => sum + parseFloat(e.amount || "0"), 0);
    };

    const buildRows = (cats: { key: string; label: string }[]) =>
      cats.map((cat) => ({
        label: cat.label,
        ytd: getYTD(cat.key),
        months: months.map((m) => getMonthTotal(cat.key, m.key)),
      }));

    // Task #4906 — resolve every PadSplit category independently. A synced
    // zero is authoritative only for the same account/property/company/month/
    // category; it must not suppress another category's historical notes.
    const resolvedPadsplitByMonth = new Map(
      months.map((m) => [
        m.key,
        resolvePnlPadsplitCategoriesForMonth(filteredExpenses, filteredIncome, m.key),
      ]),
    );
    const getResolvedPadsplitMonth = (catKey: string, monthKey: string): number =>
      resolvedPadsplitByMonth.get(monthKey)?.[catKey] ?? 0;
    const getResolvedPadsplitYTD = (catKey: string): number => {
      const y = new Date().getFullYear();
      return months.reduce(
        (sum, m) => sum + (m.key.startsWith(String(y)) ? getResolvedPadsplitMonth(catKey, m.key) : 0),
        0,
      );
    };

    const collectionRows = collectionCategories.map((cat) => ({
      key: cat.key,
      label: cat.label,
      ytd: getResolvedPadsplitYTD(cat.key),
      months: months.map((m) => getResolvedPadsplitMonth(cat.key, m.key)),
    }));
    const expenseRows = Object.entries(expenseCategoryGroups).map(([label, cats]) => ({
      label,
      ytd: getGroupYTD(cats, propertyExpenses),
      months: months.map((m) => getGroupMonthTotal(cats, m.key, propertyExpenses)),
    })).filter((row) => row.ytd !== 0 || row.months.some((v) => v !== 0));

    // Task #3788 — Company Operations: non-property (company-level) expenses
    // surfaced as their own labeled group, broken down by category. Only P&L
    // (non-balance-sheet) categories are included so it mirrors the property
    // expense rows; balance-sheet/CapEx stay in the balance-sheet section.
    const companyOperationsRows = [
      ...Object.entries(expenseCategoryGroups).map(([label, cats]) => ({
        label,
        ytd: getGroupYTD(cats, companyExpenses),
        months: months.map((m) => getGroupMonthTotal(cats, m.key, companyExpenses)),
      })),
      ...nonBsCustomCats.map((c) => ({
        label: c.label,
        ytd: getYTD(c.value, companyExpenses),
        months: months.map((m) => getMonthTotal(c.value, m.key, companyExpenses)),
      })),
    ].filter((row) => row.ytd !== 0 || row.months.some((v) => v !== 0));
    const totalCompanyOperations = {
      label: "Total Company Operations",
      ytd: companyOperationsRows.reduce((s, r) => s + r.ytd, 0),
      months: months.map((_, i) => companyOperationsRows.reduce((s, r) => s + r.months[i], 0)),
    };
    const adjustmentRows = buildRows(adjustmentCategories);
    // Task #2301 — Charges as a separate PadSplit expense group.
    const padsplitChargeRows = buildRows(padsplitChargeCategories).map((r, i) => ({
      ...r,
      key: padsplitChargeCategories[i].key,
    })).filter(
      (r) => r.ytd !== 0 || r.months.some((v) => v !== 0),
    );

    // Task #2311 — non-BS custom categories appear in P&L Expenses section.
    // Task #3788 — property-level only; company-level customs go to the
    // Company Operations group above.
    const customExpenseRows = nonBsCustomCats.map((c) => ({
      label: c.label,
      ytd: getYTD(c.value, propertyExpenses),
      months: months.map((m) => getMonthTotal(c.value, m.key, propertyExpenses)),
    })).filter((row) => row.ytd !== 0 || row.months.some((v) => v !== 0));

    // Task #2311 — BS custom categories are excluded from P&L totals.
    // Task #2331 — built-in CapEx is also a balance-sheet category and is
    // excluded from operating-expense totals to match its Settings label.
    const balanceSheetRows = [
      {
        label: "CapEx",
        ytd: getYTD("capex"),
        months: months.map((m) => getMonthTotal("capex", m.key)),
      },
      ...allCustomCats
        .filter((c) => c.isBalanceSheet)
        .map((c) => ({
          label: c.label,
          ytd: getYTD(c.value),
          months: months.map((m) => getMonthTotal(c.value, m.key)),
        })),
    ].filter((row) => row.ytd !== 0 || row.months.some((v) => v !== 0));

    const uncategorizedRow = {
      label: "Uncategorized (excluded from totals)",
      ytd: getGroupYTD(["other"]),
      months: months.map((m) => getGroupMonthTotal(["other"], m.key)),
    };

    const totalCollections = {
      label: "Total collections",
      ytd: collectionRows.reduce((s, r) => s + r.ytd, 0),
      months: months.map((_, i) => collectionRows.reduce((s, r) => s + r.months[i], 0)),
    };

    // Task #2311 — include non-BS custom expense rows in the P&L total.
    // Task #3788 — include the broken-out Company Operations rows so the total
    // is unchanged vs. when they were lumped into the property category rows.
    const totalExpenses = {
      label: "Total expenses",
      ytd: [...expenseRows, ...customExpenseRows, ...companyOperationsRows].reduce((s, r) => s + r.ytd, 0),
      months: months.map((_, i) => [...expenseRows, ...customExpenseRows, ...companyOperationsRows].reduce((s, r) => s + r.months[i], 0)),
    };

    const totalAdjustments = {
      label: "Total adjustments",
      ytd: adjustmentRows.reduce((s, r) => s + r.ytd, 0),
      months: months.map((_, i) => adjustmentRows.reduce((s, r) => s + r.months[i], 0)),
    };

    const totalPayout = {
      label: "Total payout",
      ytd: totalCollections.ytd - totalExpenses.ytd + totalAdjustments.ytd,
      months: months.map((_, i) => totalCollections.months[i] - totalExpenses.months[i] + totalAdjustments.months[i]),
    };

    // Task #2301 (revised) — PadSplit fee rows: expense records OR notes fallback.
    // Task #2368 — Render as POSITIVE amounts that ADD to Total expenses (so
    // Profit goes down). Notes-based fallback already returns positive values
    // and expense records are stored as negative, so Math.abs normalizes both.
    const padsplitFeeCategories = [
      { key: "padsplit_booking_fees", label: "[PadSplit] Booking fees" },
      { key: "padsplit_service_fees", label: "[PadSplit] Platform / Service fees" },
    ];
    const padsplitFeeRows = padsplitFeeCategories
      .map((cat) => ({
        key: cat.key,
        label: cat.label,
        ytd: Math.abs(getResolvedPadsplitYTD(cat.key)),
        months: months.map((m) => Math.abs(getResolvedPadsplitMonth(cat.key, m.key))),
      }))
      .filter((r) => r.ytd !== 0 || r.months.some((v) => v !== 0));

    return { collectionRows, expenseRows, companyOperationsRows, totalCompanyOperations, adjustmentRows, padsplitChargeRows, padsplitFeeRows, totalCollections, totalExpenses, totalAdjustments, totalPayout, uncategorizedRow, customExpenseRows, balanceSheetRows, balanceSheetCatValues };
  }, [filteredExpenses, filteredIncome, months, pnlCatSettings]);

  // Declared here (before incomeData useMemo) so it is in scope for the memo.
  const catSettingsQuery = useQuery<ExpenseCategorySettings>({ queryKey: ["/api/app/expense-category-settings"] });

  // Task #2301 — Build Income section from /api/app/income ledger. The old
  // single "PadSplit earnings" net-payout row is replaced by discrete gross
  // income rows (dues, late fees) parsed from income_transactions.notes, and
  // new PadSplit fee expense rows (booking fees, service fees) also parsed
  // from notes. Math reconciles: replacing net with (gross − fees) and adding
  // fees to expenses leaves Profit unchanged.
  const incomeData = useMemo(() => {
    // Task #2301 — padsplit_earnings net-payout source removed; breakdown
    // rows derived from notes take its place in the income section.
    // Manual income is grouped by category so custom income categories each
    // appear as their own P&L row.
    const customIncomeCats = ((catSettingsQuery.data as any)?.customIncomeCategories as { label: string; value: string }[]) || [];
    const allIncomeCatMap = new Map<string, string>(
      [...INCOME_CATEGORIES, ...customIncomeCats].map((c) => [c.value, c.label])
    );

    // Task #3788 — partition company-level (non-property) income so it can be
    // surfaced as its own labeled "Company Operations" group (mirroring the
    // expense breakout) without double-counting into the property income rows.
    const companyIncome = filteredIncome.filter((r) => (r as any).isCompanyLevel);
    const propertyIncome = filteredIncome.filter((r) => !(r as any).isCompanyLevel);

    // Task #2301 (revised) — padsplit_earnings income_transactions excluded
    // from P&L. The expense records (membership_dues, late_fees, etc.) are the
    // authoritative gross breakdown; using the net income_transaction too would
    // double-count. incomeRows = mercury_credit + manual (by category) only.
    // Task #3788 — buildPnlIncomeRows (in _shared) drives both the property
    // income rows and the Company Operations income group from an explicit list.
    const incomeRows = buildPnlIncomeRows(propertyIncome, months, allIncomeCatMap);

    // Task #3788 — Company Operations income: company-level (non-property)
    // Mercury credits / manual income surfaced as their own labeled group.
    const companyOperationsIncomeRows = buildPnlIncomeRows(companyIncome, months, allIncomeCatMap, "co_");
    const totalCompanyOperationsIncome = {
      label: "Total Company Operations income",
      ytd: companyOperationsIncomeRows.reduce((s, r) => s + r.ytd, 0),
      months: months.map((_, i) => companyOperationsIncomeRows.reduce((s, r) => s + r.months[i], 0)),
    };

    // Task #3788 — total income includes the broken-out Company Operations rows
    // so the combined total is unchanged vs. when they were lumped into the
    // property income rows.
    const totalIncome = {
      label: "Total income",
      ytd: [...incomeRows, ...companyOperationsIncomeRows].reduce((s, r) => s + r.ytd, 0),
      months: months.map((_, i) => [...incomeRows, ...companyOperationsIncomeRows].reduce((s, r) => s + r.months[i], 0)),
    };

    // Task #2274 — Build paysplit payout breakdown rows from income_transactions
    // notes so the P&L can render informational sub-lines under PadSplit earnings.
    const padsplitRows = filteredIncome.filter((r) => r.source === "padsplit_earnings");
    const hasPadsplitBreakdown = padsplitRows.some((r) => parsePadsplitBreakdown(r.notes) !== null);

    type PSBreakdownKey = "dues" | "lateFees" | "bookingFees" | "serviceFees";

    const getPSBreakdownMonthTotal = (component: PSBreakdownKey, monthKey: string) =>
      padsplitRows
        .filter((r) => r.date.startsWith(monthKey))
        .reduce((sum, r) => {
          const bd = parsePadsplitBreakdown(r.notes);
          return sum + (bd ? bd[component] : 0);
        }, 0);

    const getPSBreakdownYTD = (component: PSBreakdownKey) => {
      const year = new Date().getFullYear();
      return padsplitRows
        .filter((r) => r.date.startsWith(String(year)))
        .reduce((sum, r) => {
          const bd = parsePadsplitBreakdown(r.notes);
          return sum + (bd ? bd[component] : 0);
        }, 0);
    };

    const rawBreakdownRows: Array<{ key: string; label: string; component: PSBreakdownKey; deduction: boolean }> =
      hasPadsplitBreakdown ? [
        { key: "ps_dues", label: "Gross dues", component: "dues", deduction: false },
        { key: "ps_late_fees", label: "Late fees", component: "lateFees", deduction: false },
        { key: "ps_booking_fees", label: "Less: Booking fees", component: "bookingFees", deduction: true },
        { key: "ps_service_fees", label: "Less: Service fees", component: "serviceFees", deduction: true },
      ] : [];

    const padsplitBreakdownRows = rawBreakdownRows
      .map((r) => ({
        key: r.key,
        label: r.label,
        deduction: r.deduction,
        ytd: r.deduction ? -getPSBreakdownYTD(r.component) : getPSBreakdownYTD(r.component),
        months: months.map((m) => {
          const mv = getPSBreakdownMonthTotal(r.component, m.key);
          return r.deduction ? -mv : mv;
        }),
      }))
      .filter((r) => r.ytd !== 0 || r.months.some((v) => v !== 0));

    return { incomeRows, companyOperationsIncomeRows, totalCompanyOperationsIncome, totalIncome, padsplitBreakdownRows };
  }, [filteredIncome, months, catSettingsQuery.data]);

  const combinedTotalIncome = useMemo(
    () => ({
      label: "Total income",
      ytd: pnlData.totalCollections.ytd + pnlData.totalAdjustments.ytd + incomeData.totalIncome.ytd,
      months: months.map(
        (_, i) => pnlData.totalCollections.months[i] + pnlData.totalAdjustments.months[i] + incomeData.totalIncome.months[i],
      ),
    }),
    [pnlData.totalCollections, pnlData.totalAdjustments, incomeData.totalIncome, months]
  );

  // Task #2409 — Total expenses = regular expenses + PadSplit charge rows.
  // padsplitFeeRows (booking/service fees) are intentionally excluded here
  // because they are already surfaced as Income deductions ("Less: Booking
  // fees" / "Less: Service fees") under Host Earnings. Adding them to
  // expenses would double-count the fees and understate Profit. The fee
  // rows still render in the Expenses section below for transparency, but
  // are excluded from the Total expenses sum (matching the export behavior
  // established in Task #2379).
  const totalExpensesWithPS = useMemo(
    () => ({
      label: "Total expenses",
      ytd: pnlData.totalExpenses.ytd
        + pnlData.padsplitChargeRows.reduce((s, r) => s + r.ytd, 0),
      months: months.map(
        (_, i) => pnlData.totalExpenses.months[i]
          + pnlData.padsplitChargeRows.reduce((s, r) => s + r.months[i], 0),
      ),
    }),
    [pnlData.totalExpenses, pnlData.padsplitChargeRows, months],
  );

  const profitRow = useMemo(
    () => ({
      label: "Profit",
      // YTD stays with the existing memo-derived total for performance.
      ytd: combinedTotalIncome.ytd - totalExpensesWithPS.ytd,
      // Per-month values use the shared authoritative formula so the P&L
      // and the dashboard KPI are guaranteed to match.
      months: months.map((m) =>
        computePnlProfitForMonth(filteredExpenses, filteredIncome, pnlCatSettings, m.key),
      ),
    }),
    [combinedTotalIncome, totalExpensesWithPS, months, filteredExpenses, filteredIncome, pnlCatSettings],
  );

  // Task #2129 — count of active uncategorized expenses in the current
  // P&L scope so we can surface a "clean these up" prompt at the top.
  const uncategorizedCount = useMemo(
    () => filteredExpenses.filter((e) => e.category === "other" && !e.voidedAt && !(e as any).excludedAt).length,
    [filteredExpenses],
  );

  const goReviewUncategorized = () => {
    const params = new URLSearchParams();
    params.set("category", "other");
    if (propertyFilter !== "all") params.set("propertyId", propertyFilter);
    navigate(`/app/financials/expenses?${params.toString()}`);
  };

  const [collectionsOpen, setCollectionsOpen] = useState(true);
  const [incomeOpen, setIncomeOpen] = useState(true);
  const [expensesOpen, setExpensesOpen] = useState(true);
  const [balanceSheetOpen, setBalanceSheetOpen] = useState(false);

  const selectableProperties = properties.filter((p) => p.status !== "archive");

  const currentMonthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }, []);
  const ownerScopeRequired = ownerFilter !== "all";
  const requiredLedgerError =
    expensesQuery.error ??
    incomeQuery.error ??
    (ownerScopeRequired ? pnlGroupsQuery.error : null);
  const requiredLedgersReady =
    expensesQuery.isSuccess &&
    incomeQuery.isSuccess &&
    (!ownerScopeRequired || pnlGroupsQuery.isSuccess);
  const retryRequiredLedgers = () => {
    if (!expensesQuery.isSuccess) void expensesQuery.refetch();
    if (!incomeQuery.isSuccess) void incomeQuery.refetch();
    if (ownerScopeRequired && !pnlGroupsQuery.isSuccess) void pnlGroupsQuery.refetch();
  };
  const requiredDataFetching =
    expensesQuery.isFetching ||
    incomeQuery.isFetching ||
    (ownerScopeRequired && pnlGroupsQuery.isFetching);
  const refreshReport = () => {
    void expensesQuery.refetch();
    void incomeQuery.refetch();
    void pnlCatSettingsQuery.refetch();
    if (ownerScopeRequired) void pnlGroupsQuery.refetch();
  };

  return (
    <div data-testid="pnl-tab">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <DateRangeFilter
          preset={datePreset}
          customFrom={dateFrom}
          customTo={dateTo}
          onPresetChange={setDatePreset}
          onCustomFromChange={setDateFrom}
          onCustomToChange={setDateTo}
          triggerClassName="w-44 text-sm"
        />
        <SearchableSelect
          value={propertyFilter}
          onValueChange={setPropertyFilter}
          searchPlaceholder="Search properties..."
          emptyText="No properties match."
          testId="select-pnl-property"
          className="w-44 text-sm"
          triggerProps={{ "data-tour": "financials-filters" }}
          options={[
            { value: "all", label: "All Properties" },
            ...sortPropertiesParentFirst(selectableProperties).map((p) => {
              const childCount = properties.filter((c) => c.parentPropertyId === p.id).length;
              const isChild = !!p.parentPropertyId;
              return {
                value: String(p.id),
                label: `${isChild ? `  \u2514 ${p.name}` : p.name}${childCount > 0 ? ` (+${childCount} units)` : ""}`,
                keywords: `${p.name ?? ""} ${p.address ?? ""}`,
              };
            }),
          ]}
        />
        {pnlOwners.length > 0 && (
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-44 text-sm" data-testid="select-pnl-owner">
              <SelectValue placeholder="All Owners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="pnl-owner-option-all">All Owners</SelectItem>
              {pnlOwners.map((o) => (
                <SelectItem key={o.id} value={String(o.id)} data-testid={`pnl-owner-option-${o.id}`}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex items-center gap-1.5"
            onClick={refreshReport}
            disabled={requiredDataFetching}
            data-testid="button-refresh-pnl"
          >
            {requiredDataFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh report
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1.5"
            onClick={handleCopyLink}
            data-testid="button-copy-pnl-link"
          >
            <Link2 className="h-4 w-4" />
            Copy link
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5"
                disabled={!requiredLedgersReady}
                data-testid="button-export-pnl" data-tour="financials-export"
              >
                <Download className="h-4 w-4" />
                Export
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid="menuitem-export-pnl-csv"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (propertyFilter !== "all") params.set("propertyId", propertyFilter);
                  if (ownerFilter !== "all") params.set("ownerId", ownerFilter);
                  if (pnlDateRange.from) params.set("dateFrom", pnlDateRange.from);
                  if (pnlDateRange.to) params.set("dateTo", pnlDateRange.to);
                  const qs = params.toString();
                  window.location.href = `/api/app/financials/pnl/export${qs ? `?${qs}` : ""}`;
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="menuitem-export-pnl-pdf"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (propertyFilter !== "all") params.set("propertyId", propertyFilter);
                  if (ownerFilter !== "all") params.set("ownerId", ownerFilter);
                  if (pnlDateRange.from) params.set("dateFrom", pnlDateRange.from);
                  if (pnlDateRange.to) params.set("dateTo", pnlDateRange.to);
                  const qs = params.toString();
                  window.location.href = `/api/app/financials/pnl/export.pdf${qs ? `?${qs}` : ""}`;
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                Export PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {requiredLedgersReady && requiredDataFetching && (
        <div
          className="mb-4 flex items-center gap-2 text-sm text-gray-500"
          role="status"
          data-testid="pnl-refreshing-indicator"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Refreshing report data…
        </div>
      )}

      {uncategorizedCount > 0 && (
        <div
          className="financials-review-notice flex flex-wrap items-center justify-between gap-3 mb-4 px-4 py-3 rounded-lg border"
          data-testid="banner-uncategorized-pnl"
        >
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <strong data-testid="text-uncategorized-count-pnl">{uncategorizedCount}</strong>{" "}
              uncategorized {uncategorizedCount === 1 ? "expense is" : "expenses are"} excluded from your P&amp;L totals.
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="financials-review-action"
            onClick={goReviewUncategorized}
            data-testid="button-review-uncategorized-pnl"
          >
            Review uncategorized
          </Button>
        </div>
      )}

      {(() => {
        if (propertyFilter === "all") return null;
        const selectedId = parseInt(propertyFilter);
        const childProps = properties.filter((p) => p.parentPropertyId === selectedId);
        if (childProps.length === 0) return null;
        return (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-sm" data-testid="banner-financials-rollup">
            <Building2 className="h-4 w-4 text-blue-500 shrink-0" />
            <span className="text-blue-800 dark:text-blue-200">
              Showing combined financials for this property and {childProps.length} child unit{childProps.length > 1 ? "s" : ""}: {childProps.map((c) => c.name).join(", ")}
            </span>
          </div>
        );
      })()}

      {!requiredLedgersReady ? (
        <div
          className={`rounded-lg border px-5 py-8 ${
            requiredLedgerError
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-gray-200 bg-gray-50 text-gray-700"
          }`}
          data-testid={requiredLedgerError ? "pnl-ledger-error" : "pnl-ledger-loading"}
          role={requiredLedgerError ? "alert" : "status"}
        >
          <div className="flex items-start gap-3">
            {requiredLedgerError ? (
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 mt-0.5" />
            ) : (
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-gray-500 mt-0.5" />
            )}
            <div>
              <p className="font-semibold">
                {requiredLedgerError ? "Profit & Loss couldn't be loaded" : "Loading Profit & Loss ledgers"}
              </p>
              <p className="mt-1 text-sm">
                {requiredLedgerError
                  ? parseErrorMessage(requiredLedgerError, "Required ledger or owner scope data is unavailable. Try again.")
                  : ownerScopeRequired && !pnlGroupsQuery.isSuccess
                    ? "Waiting for the expense and income ledgers and selected owner's property scope before calculating totals."
                    : "Waiting for the expense and income ledgers before calculating totals."}
              </p>
              {requiredLedgerError && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-4 border-red-300 bg-white text-red-800 hover:bg-red-100"
                  onClick={retryRequiredLedgers}
                  disabled={requiredDataFetching}
                  data-testid="button-retry-pnl-ledgers"
                >
                  {requiredDataFetching ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Retry
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
      <>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm" data-testid="pnl-table">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-4 py-3 text-left font-medium text-gray-500 min-w-[180px]">Category</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500 min-w-[120px]">YTD</th>
              {months.map((m) => {
                const isInflight = m.key === currentMonthKey;
                return (
                  <th key={m.key} className="px-4 py-3 text-left font-medium text-gray-500 min-w-[110px] whitespace-nowrap">
                    {isInflight ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex flex-col gap-0.5 cursor-default">
                              <span className="italic">{m.label}</span>
                              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 font-medium leading-none w-fit">
                                In flight
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[220px] text-xs">
                            PadSplit income for this month posts around the 5th of the following month, so this number isn't final yet.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : m.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* ── Income ─────────────────────────────────────────────────── */}
            <tr className="bg-gray-50">
              <td colSpan={2 + months.length} className="px-4 py-2 text-sm font-bold text-gray-800 uppercase tracking-wide">
                Income
              </td>
            </tr>

            {/* Member collections sub-section — all collection rows are PadSplit-sourced */}
            <tr>
              <td colSpan={2 + months.length} className="px-4 py-2">
                <button onClick={() => setCollectionsOpen((v) => !v)} className="flex items-center gap-2 text-sm font-semibold text-gray-700" data-testid="toggle-collections">
                  {collectionsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  Member collections
                </button>
              </td>
            </tr>
            {collectionsOpen && pnlData.collectionRows.map((row) => (
              <tr key={row.label} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`row-pnl-collection-${row.key}`}>
                <td className="px-4 py-2.5 text-gray-700">
                  {row.label}
                  <PadSplitBadge />
                </td>
                <td className="px-4 py-2.5 font-medium">{fmtCurrency(row.ytd)}</td>
                {row.months.map((val, i) => (
                  <td key={i} className={`px-4 py-2.5 ${val < 0 ? "text-red-600" : "text-blue-600"}`}>
                    {fmtCurrency(val)}
                  </td>
                ))}
              </tr>
            ))}

            {/* Adjustments — credits + referral rewards in Income with PadSplit badge.
                Note: "Charges" moved to Expenses section (padsplitChargeRows). */}
            {pnlData.adjustmentRows.filter((r) => r.ytd !== 0 || r.months.some((v) => v !== 0)).map((row) => (
              <tr key={row.label} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-700 pl-6">
                  {row.label}
                  <PadSplitBadge />
                </td>
                <td className="px-4 py-2.5 font-medium">{fmtCurrency(row.ytd)}</td>
                {row.months.map((val, i) => (
                  <td key={i} className={`px-4 py-2.5 ${val < 0 ? "text-red-600" : "text-green-600"}`}>
                    {fmtCurrency(val)}
                  </td>
                ))}
              </tr>
            ))}

            {/* Other income (Mercury credits, Manual income) */}
            <tr>
              <td colSpan={2 + months.length} className="px-4 py-2">
                <button onClick={() => setIncomeOpen((v) => !v)} className="flex items-center gap-2 text-sm font-semibold text-gray-700" data-testid="toggle-income-section">
                  {incomeOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  Other income
                </button>
              </td>
            </tr>
            {incomeOpen && incomeData.incomeRows.length === 0 && (
              <tr className="border-b border-gray-50">
                <td colSpan={2 + months.length} className="px-4 py-2.5 text-gray-400 italic text-xs" data-testid="text-pnl-income-empty">
                  No other income recorded. Connect Mercury or add manual income entries to populate this section.
                </td>
              </tr>
            )}
            {incomeOpen && incomeData.incomeRows.map((row) => (
              <tr key={row.key} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`row-pnl-income-${row.key}`}>
                <td className="px-4 py-2.5 text-gray-700">
                  {row.label}
                  {(row as any).isPadSplit && <PadSplitBadge />}
                </td>
                <td className="px-4 py-2.5 font-medium text-green-700">{fmtCurrency(row.ytd)}</td>
                {row.months.map((val, i) => (
                  <td key={i} className={`px-4 py-2.5 ${val < 0 ? "text-red-600" : "text-green-700"}`}>
                    {fmtCurrency(val)}
                  </td>
                ))}
              </tr>
            ))}

            {/* Task #3788 — Company Operations income: company-level (non-property) income as its own labeled group */}
            {incomeOpen && incomeData.companyOperationsIncomeRows.length > 0 && (
              <tr className="bg-gray-50/60">
                <td colSpan={2 + months.length} className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500" data-testid="text-pnl-income-company-operations-header">
                  Company Operations
                </td>
              </tr>
            )}
            {incomeOpen && incomeData.companyOperationsIncomeRows.map((row) => (
              <tr key={row.key} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`row-pnl-income-${row.key}`}>
                <td className="px-4 py-2.5 text-gray-700 pl-8">
                  {row.label}
                  {(row as any).isPadSplit && <PadSplitBadge />}
                </td>
                <td className="px-4 py-2.5 font-medium text-green-700">{fmtCurrency(row.ytd)}</td>
                {row.months.map((val, i) => (
                  <td key={i} className={`px-4 py-2.5 ${val < 0 ? "text-red-600" : "text-green-700"}`}>
                    {fmtCurrency(val)}
                  </td>
                ))}
              </tr>
            ))}
            {incomeOpen && incomeData.companyOperationsIncomeRows.length > 0 && (
              <tr className="border-b border-gray-100 bg-gray-50/60" data-testid="row-pnl-income-total-company-operations">
                <td className="px-4 py-2 text-sm font-medium text-gray-600 pl-8">{incomeData.totalCompanyOperationsIncome.label}</td>
                <td className="px-4 py-2 font-semibold text-green-700">{fmtCurrency(incomeData.totalCompanyOperationsIncome.ytd)}</td>
                {incomeData.totalCompanyOperationsIncome.months.map((val, i) => (
                  <td key={i} className={`px-4 py-2 font-medium ${val < 0 ? "text-red-600" : "text-green-700"}`}>
                    {fmtCurrency(val)}
                  </td>
                ))}
              </tr>
            )}

            {/* Total income — combines host earnings + adjustments + other income */}
            <tr className="border-b border-gray-200 bg-gray-50" data-testid="row-pnl-total-income">
              <td className="px-4 py-2.5 font-semibold text-gray-900">{combinedTotalIncome.label}</td>
              <td className="px-4 py-2.5 font-bold">{fmtCurrency(combinedTotalIncome.ytd)}</td>
              {combinedTotalIncome.months.map((val, i) => {
                const isInflight = months[i].key === currentMonthKey;
                return (
                  <td key={i} className="px-4 py-2.5 font-semibold">
                    {isInflight ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex flex-col gap-0.5 cursor-default">
                              <span className="italic">{fmtCurrency(val)}</span>
                              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 font-medium leading-none w-fit">
                                In flight
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[220px] text-xs">
                            PadSplit income for this month posts around the 5th of the following month, so this number isn't final yet.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : fmtCurrency(val)}
                  </td>
                );
              })}
            </tr>

            {/* ── Expenses ────────────────────────────────────────────────── */}
            <tr>
              <td colSpan={2 + months.length} className="px-4 py-2">
                <button onClick={() => setExpensesOpen((v) => !v)} className="flex items-center gap-2 text-sm font-semibold text-gray-700" data-testid="toggle-expenses-section">
                  {expensesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  Expenses
                </button>
              </td>
            </tr>
            {expensesOpen && pnlData.expenseRows.map((row) => (
              <tr key={row.label} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-700">{row.label}</td>
                <td className="px-4 py-2.5 font-medium">{fmtCurrency(row.ytd)}</td>
                {row.months.map((val, i) => (
                  <td key={i} className={`px-4 py-2.5 ${val < 0 ? "text-red-600" : "text-blue-600"}`}>
                    {fmtCurrency(val)}
                  </td>
                ))}
              </tr>
            ))}
            {/* Task #3788 — Company Operations: company-level (non-property) expenses as their own labeled group */}
            {expensesOpen && pnlData.companyOperationsRows.length > 0 && (
              <tr className="bg-indigo-50/60 dark:bg-indigo-950/20 border-y border-indigo-100" data-testid="row-pnl-company-operations-header">
                <td colSpan={2 + months.length} className="px-4 py-1.5">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                    <Building2 className="h-3.5 w-3.5" />
                    Company Operations
                  </span>
                </td>
              </tr>
            )}
            {expensesOpen && pnlData.companyOperationsRows.map((row) => (
              <tr key={`co-${row.label}`} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`row-pnl-company-op-${row.label}`}>
                <td className="px-4 py-2.5 text-gray-700 pl-8">{row.label}</td>
                <td className="px-4 py-2.5 font-medium">{fmtCurrency(row.ytd)}</td>
                {row.months.map((val, i) => (
                  <td key={i} className={`px-4 py-2.5 ${val < 0 ? "text-red-600" : "text-blue-600"}`}>
                    {fmtCurrency(val)}
                  </td>
                ))}
              </tr>
            ))}
            {expensesOpen && pnlData.companyOperationsRows.length > 0 && (
              <tr className="border-b border-indigo-100 bg-indigo-50/30" data-testid="row-pnl-company-operations-total">
                <td className="px-4 py-2 font-semibold text-indigo-800 dark:text-indigo-300 pl-8">{pnlData.totalCompanyOperations.label}</td>
                <td className="px-4 py-2 font-bold">{fmtCurrency(pnlData.totalCompanyOperations.ytd)}</td>
                {pnlData.totalCompanyOperations.months.map((val, i) => (
                  <td key={i} className="px-4 py-2 font-semibold">{fmtCurrency(val)}</td>
                ))}
              </tr>
            )}
            {/* Task #2301 — PadSplit Charges (moved from Income adjustments) as expense rows */}
            {expensesOpen && pnlData.padsplitChargeRows.map((row) => (
              <tr key={row.label} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`row-pnl-ps-charge-${row.label}`}>
                <td className="px-4 py-2.5 text-gray-700">
                  {row.label}
                  <PadSplitBadge />
                </td>
                <td className="px-4 py-2.5 font-medium text-red-600">{fmtCurrency(row.ytd)}</td>
                {row.months.map((val, i) => (
                  <td key={i} className={`px-4 py-2.5 ${val > 0 ? "text-red-600" : "text-blue-600"}`}>
                    {fmtCurrency(val)}
                  </td>
                ))}
              </tr>
            ))}
            {/* Task #2301 (revised) — PadSplit platform fees (booking + service) from expense records */}
            {expensesOpen && pnlData.padsplitFeeRows.map((row) => (
              <tr key={(row as any).key} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`row-pnl-ps-fee-${(row as any).key}`}>
                <td className="px-4 py-2.5 text-gray-700">
                  {row.label}
                  <PadSplitBadge />
                </td>
                <td className="px-4 py-2.5 font-medium text-red-600">{fmtCurrency(row.ytd)}</td>
                {row.months.map((val, i) => (
                  <td key={i} className={`px-4 py-2.5 ${val > 0 ? "text-red-600" : "text-blue-600"}`}>
                    {fmtCurrency(val)}
                  </td>
                ))}
              </tr>
            ))}
            {/* Task #2311 — non-BS custom expense rows appear in the Expenses section */}
            {expensesOpen && pnlData.customExpenseRows.map((row) => (
              <tr key={`custom-${row.label}`} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-700">{row.label}</td>
                <td className="px-4 py-2.5 font-medium">{fmtCurrency(row.ytd)}</td>
                {row.months.map((val, i) => (
                  <td key={i} className={`px-4 py-2.5 ${val < 0 ? "text-red-600" : "text-blue-600"}`}>
                    {fmtCurrency(val)}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-b border-gray-200 bg-gray-50">
              <td className="px-4 py-2.5 font-semibold text-gray-900">{totalExpensesWithPS.label}</td>
              <td className="px-4 py-2.5 font-bold">{fmtCurrency(totalExpensesWithPS.ytd)}</td>
              {totalExpensesWithPS.months.map((val, i) => {
                const isInflight = months[i].key === currentMonthKey;
                return (
                  <td key={i} className="px-4 py-2.5 font-semibold">
                    {isInflight ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex flex-col gap-0.5 cursor-default">
                              <span className="italic">{fmtCurrency(val)}</span>
                              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 font-medium leading-none w-fit">
                                In flight
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[220px] text-xs">
                            PadSplit income for this month posts around the 5th of the following month, so this number isn't final yet.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : fmtCurrency(val)}
                  </td>
                );
              })}
            </tr>

            {/* ── Profit ──────────────────────────────────────────────────── */}
            <tr className="border-t-2 border-gray-300 bg-blue-50 dark:bg-blue-950/40" data-testid="row-pnl-profit">
              <td className="px-4 py-3 font-bold text-blue-900 dark:text-blue-100 text-base">{profitRow.label}</td>
              <td className={`px-4 py-3 font-bold text-base ${profitRow.ytd < 0 ? "text-red-700" : "text-blue-900 dark:text-blue-100"}`}>
                {fmtCurrency(profitRow.ytd)}
              </td>
              {profitRow.months.map((val, i) => {
                const isInflight = months[i].key === currentMonthKey;
                return (
                  <td key={i} className={`px-4 py-3 font-bold ${val < 0 ? "text-red-700" : "text-blue-900 dark:text-blue-100"}`}>
                    {isInflight ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex flex-col gap-0.5 cursor-default">
                              <span className="italic">{fmtCurrency(val)}</span>
                              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 font-medium leading-none w-fit">
                                In flight
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[220px] text-xs">
                            PadSplit income for this month posts around the 5th of the following month, so this number isn't final yet.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : fmtCurrency(val)}
                  </td>
                );
              })}
            </tr>

            {/* ── Uncategorized (excluded from totals) ────────────────────── */}
            {(pnlData.uncategorizedRow.ytd !== 0 || pnlData.uncategorizedRow.months.some((v) => v !== 0)) && (
              <tr className="border-t border-dashed border-gray-200">
                <td className="px-4 py-2.5 text-gray-400 italic text-xs">{pnlData.uncategorizedRow.label}</td>
                <td className="px-4 py-2.5 text-gray-400 text-xs">{fmtCurrency(pnlData.uncategorizedRow.ytd)}</td>
                {pnlData.uncategorizedRow.months.map((val, i) => (
                  <td key={i} className="px-4 py-2.5 text-gray-400 text-xs">{fmtCurrency(val)}</td>
                ))}
              </tr>
            )}

            {/* ── Balance Sheet Items (Task #2311) ─────────────────────────── */}
            {pnlData.balanceSheetRows.length > 0 && (
              <>
                <tr className="border-t border-dashed border-gray-300">
                  <td colSpan={2 + months.length} className="px-4 py-2">
                    <button
                      onClick={() => setBalanceSheetOpen((v) => !v)}
                      className="flex items-center gap-2 text-sm font-semibold text-gray-500"
                      data-testid="toggle-balance-sheet-section"
                    >
                      {balanceSheetOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Balance Sheet Items
                      <span className="text-xs font-normal text-gray-400">(excluded from P&amp;L totals)</span>
                    </button>
                  </td>
                </tr>
                {balanceSheetOpen && pnlData.balanceSheetRows.map((row) => (
                  <tr key={`bs-${row.label}`} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-500 italic text-sm">{row.label}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-sm">{fmtCurrency(row.ytd)}</td>
                    {row.months.map((val, i) => (
                      <td key={i} className="px-4 py-2.5 text-gray-500 text-sm">{fmtCurrency(val)}</td>
                    ))}
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
      {excludedMercuryInView.length > 0 && (
        <div
          className="mt-3 text-xs text-gray-500"
          data-testid="text-pnl-excluded-mercury-note"
        >
          <button
            type="button"
            onClick={handleViewExcludedMercury}
            className="underline decoration-dotted underline-offset-2 hover:text-gray-700"
            data-testid="link-pnl-excluded-mercury"
          >
            {excludedMercuryInView.length} Mercury transaction{excludedMercuryInView.length === 1 ? "" : "s"} excluded from totals
          </button>
          <span className="ml-1">— click to review on the Expenses tab.</span>
        </div>
      )}
      {excludedIncomeInView.length > 0 && (
        <div
          className="mt-2 text-xs text-gray-500"
          data-testid="text-pnl-excluded-income-note"
        >
          <button
            type="button"
            onClick={handleViewExcludedIncome}
            className="underline decoration-dotted underline-offset-2 hover:text-gray-700"
            data-testid="link-pnl-excluded-income"
          >
            {excludedIncomeInView.length} income transaction{excludedIncomeInView.length === 1 ? "" : "s"} excluded from totals
          </button>
          <span className="ml-1">— click to review on the Income tab.</span>
        </div>
      )}
      </>
      )}

    </div>
  );
}

